import { type BaseRendererComponentProps, EmptyModel, EmptyModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { aiPhotoGenerationEnabled, modalConfig, PROMO_MODAL_ID } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { isOpenPopup } from 'reactApp/modules/popup/popup.selectors';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';

const LazyComponent = React.lazy(
    () => import('reactApp/ui/AiPhotoGeneration/Promo/Modal/GenerationEntryPoint/AiPhotoGenerationEntryPointModal')
);

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiPhotoGenerationPromoModalCreator: ModelCreator = ({ models, render, stores }) =>
    new EmptyModel(
        PROMO_MODAL_ID,
        models.base,
        new EmptyModelProps(PROMO_MODAL_ID, {
            store: new LocalStorageStore(PROMO_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_MODAL_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: modalConfig.showLimit,
            closeLimit: modalConfig.closeLimit,
            showInterval: modalConfig.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                // Только десктоп
                if (IS_PHONE_BROWSER || !aiPhotoGenerationEnabled) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                // Показываем только в хомяке
                if (storage !== EStorageType.home) {
                    reject();
                    return;
                }

                const isAiGenerationOpen = isOpenPopup(state, popupNames.FULLSCREEN_AI_GENERATION_IMAGE);

                // Не показываем модалку если экран генерации уже открыт
                if (isAiGenerationOpen) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isB2BUser = UserSelectors.isB2BUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);
                const isCorp = UserSelectors.isCorpUser(state);
                const isSocialUser = UserSelectors.isSocialUser(state);

                // Не показываем бизам, корпам, платникам, оверквотерам и соц аккам
                if (isBizUser || isB2BUser || isPaidUser || isCorp || isOverquota || isSocialUser) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
